<script setup lang="ts">
import type { CatalogOffering } from '~/store/billing'

/**
 * What is being bought, in lines — the selection list and the machine.
 *
 * Extracted from the order summary sidebar because the review step of the
 * commit sheet shows the same thing, and two hand-written copies of "what am I
 * buying" is how a customer comes to read two different answers to it and
 * starts wondering which one the invoice will follow. Same argument as
 * `CheckoutSocialRegisterStep` being the one billing-address form and
 * `AuthTwoFactorStep` the one second factor.
 *
 * It computes nothing: every value is handed in already resolved, so the
 * sidebar and the sheet cannot disagree even by rounding.
 */
const props = withDefaults(
  defineProps<{
    /** Only shown when the account HAS a subscription to choose between. */
    hasSubscriptionChoice?: boolean
    /** Already resolved to a name — this component does no lookups. */
    subscriptionLabel?: string | null
    locationName: string
    termLabel: string
    osName?: string | null
    offering: CatalogOffering
    totalStorageGb: number
    /**
     * Turn the values into "change" affordances.
     *
     * False in the sidebar, where every card is already on the page a scroll
     * away, and a link beside each line would just be noise. True in the sheet,
     * which covers the page — there the only way back to a decision is for this
     * to offer one.
     */
    editable?: boolean
  }>(),
  { hasSubscriptionChoice: false, subscriptionLabel: null, osName: null, editable: false },
)

const emit = defineEmits<{
  /** The disk is a line the customer can act on from either surface. */
  (e: 'edit-storage'): void
  /** Anchor of the step that owns this decision. */
  (e: 'edit-step', anchor: string): void
}>()

/** One row: a label, a value, and — when editable — a way back to it. */
const rows = computed(() => [
  ...(props.hasSubscriptionChoice
    ? [
        {
          key: 'subscription',
          label: 'billing.page.checkout.summary.subscription',
          value: props.subscriptionLabel,
          anchor: 'checkout-billing',
        },
      ]
    : []),
  {
    key: 'location',
    label: 'billing.page.checkout.summary.location',
    value: props.locationName,
    anchor: 'checkout-location',
  },
  {
    key: 'cycle',
    label: 'billing.page.checkout.summary.cycle',
    value: props.termLabel,
    anchor: 'checkout-cycle',
  },
  {
    key: 'os',
    label: 'billing.page.checkout.summary.os',
    value: props.osName,
    anchor: 'checkout-os',
  },
])

const ramGb = computed(() =>
  (props.offering.ram_mb / 1024).toFixed(props.offering.ram_mb % 1024 ? 1 : 0),
)
</script>

<template>
  <div class="space-y-3 text-sm">
    <dl class="space-y-2.5">
      <div
        v-for="row in rows"
        :key="row.key"
        class="flex items-baseline justify-between gap-3"
      >
        <dt class="text-xs text-muted shrink-0">{{ $t(row.label) }}</dt>
        <dd class="min-w-0 text-right">
          <button
            v-if="editable"
            type="button"
            class="max-w-full truncate font-medium text-primary hover:underline"
            @click="emit('edit-step', row.anchor)"
          >
            {{ row.value || '—' }}
          </button>
          <span v-else class="block truncate font-medium">
            <template v-if="row.value">{{ row.value }}</template>
            <span v-else class="text-muted">—</span>
          </span>
        </dd>
      </div>
    </dl>

    <!-- The machine, spelled out — storage included, because it is a choice
         the customer made and can still change. -->
    <div class="rounded-lg border border-default p-3 space-y-1.5">
      <div class="flex items-baseline justify-between gap-2">
        <div class="min-w-0 truncate font-semibold">{{ offering.name }}</div>
        <button
          v-if="editable"
          type="button"
          class="shrink-0 text-xs text-primary hover:underline"
          @click="emit('edit-step', 'checkout-plan')"
        >
          {{ $t('billing.page.checkout.commit.change') }}
        </button>
      </div>
      <div class="grid grid-cols-3 gap-2 text-xs">
        <div>
          <div class="text-muted">{{ $t('billing.page.checkout.plan.colVcpu') }}</div>
          <div class="font-medium">{{ offering.vcpu }}</div>
        </div>
        <div>
          <div class="text-muted">{{ $t('billing.page.checkout.plan.colMemory') }}</div>
          <div class="font-medium">{{ ramGb }} GB</div>
        </div>
        <div>
          <div class="text-muted">{{ $t('billing.page.checkout.plan.colDisk') }}</div>
          <!-- Marked because two suites need to read the disk the order is
               actually being placed with, and every other "N GB" on the page
               is a quick-size button. -->
          <button
            type="button"
            data-testid="summary-total-disk-gb"
            class="font-medium text-primary hover:underline"
            @click="emit('edit-storage')"
          >
            {{ totalStorageGb }} GB
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
